import { ENUMS } from "@/lib/api/enums";
import { chipFor } from "@/lib/status";
import { cn } from "@/lib/utils";

/** The six stages as a track the trainee moves along. `reached` is the furthest stage so far
 * (null: not started); `fresh` marks the one just unlocked so it fills in once. */
export function StageMap({
  reached,
  fresh,
  className,
}: {
  reached: string | null;
  fresh?: boolean;
  className?: string;
}) {
  const stages = ENUMS.stages;
  const at = reached ? stages.indexOf(reached) : -1;
  // Six labels need about 28rem. In a narrower box (a coach card, a phone) only the current
  // stage is named, under the track.
  return (
    <div className={cn("@container", className)}>
      <ol aria-label="The six stages" className="grid grid-cols-6 gap-1">
        {stages.map((s, i) => {
          const done = i <= at;
          const current = i === at;
          return (
            <li key={s} aria-current={current ? "step" : undefined} className="min-w-0">
              <span aria-hidden className="bg-muted block h-1.5 overflow-hidden rounded-full">
                {done && (
                  <span
                    className={cn(
                      "bg-brand block h-full rounded-full",
                      current && fresh && "stage-fill",
                    )}
                  />
                )}
              </span>
              <span
                className={cn(
                  "mt-1.5 hidden text-[11px] leading-tight @md:block",
                  current
                    ? "text-foreground font-semibold"
                    : done
                      ? "text-foreground"
                      : "text-muted-foreground",
                )}
              >
                {chipFor("stages", s).label}
                {current && <span className="sr-only"> (current)</span>}
                {done && !current && <span className="sr-only"> (done)</span>}
              </span>
            </li>
          );
        })}
      </ol>
      <p aria-hidden className="text-muted-foreground mt-1.5 text-[11px] @md:hidden">
        {at >= 0 ? (
          <>
            Stage {at + 1} of 6 ·{" "}
            <span className="text-foreground font-semibold">
              {chipFor("stages", stages[at]!).label}
            </span>
          </>
        ) : (
          `${chipFor("stages", stages[0]!).label} to ${chipFor("stages", stages[5]!).label}, six stages`
        )}
      </p>
    </div>
  );
}
